// src/oura/ui/useOuraTabTitle.ts
//
// Names the browser tab after the Oura page that is open: "Visibility · Oura",
// or "Nyonya Table · Delivery · Oura" inside a client on the staff side. The
// tab used to read the hiring product's line ("ourteam - Connecting talent
// with opportunities in Asia") on every Oura screen, in every bookmark and
// every shared link.
//
// Read from the page itself (its h1, and the sidebar's business name) rather
// than threaded through each screen, so a screen added later is named the day
// it ships. The old title comes back when the person leaves Oura.

import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const SUFFIX = "Oura";

function pageTitle(pathname: string): string {
    // The merchant's home greets them by name; the tab says where they are.
    const home = pathname === "/oura" || pathname === "/oura/" || pathname === "/oura/dashboard";
    const h1 = home ? "Home" : document.querySelector("main h1, h1")?.textContent?.trim() ?? "";
    const inBusiness = /^\/oura\/businesses\/[^/]+/.test(pathname) && !pathname.startsWith("/oura/businesses/new");
    const business = inBusiness ? document.querySelector("[data-oura-identity]")?.textContent?.trim() ?? "" : "";
    return [business, h1 && h1 !== business ? h1 : "", SUFFIX].filter(Boolean).join(" · ");
}

export function useOuraTabTitle() {
    const { pathname } = useLocation();
    useEffect(() => {
        const before = document.title;
        return () => { document.title = before; };
    }, []);
    useEffect(() => {
        const apply = () => {
            const next = pageTitle(pathname);
            if (document.title !== next) document.title = next;
        };
        apply();
        // The h1 and the business name land after their reads finish. One
        // look per frame, however many nodes changed in it.
        let frame = 0;
        const observer = new MutationObserver(() => {
            if (frame) return;
            frame = requestAnimationFrame(() => { frame = 0; apply(); });
        });
        observer.observe(document.body, { childList: true, subtree: true, characterData: true });
        return () => { observer.disconnect(); cancelAnimationFrame(frame); };
    }, [pathname]);
}
